import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Input, Select, Spin, Empty, Pagination, Alert, Rate, Tooltip } from 'antd';
import { SearchOutlined, EnvironmentOutlined, CalendarOutlined, EuroOutlined, StarOutlined, ShopOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import BackButton from '../../components/BackButton';
import FoundingPartnerBadge from '../../components/badges/FoundingPartnerBadge';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import { getCityByLanguage } from '../../utils/locationFormat';
import './ServicesPage.scss';

const { Option } = Select;
const { Search } = Input;

const ServicesPage = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { serviceStore, petStore } = useStores();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    // Fetch categories, cities, animal categories
    serviceStore.fetchServiceCategories();
    serviceStore.fetchCities();
    petStore.fetchAnimalCategories();

    // Check for partner filter in URL
    const partnerParam = searchParams.get('partner');
    if (partnerParam) {
      serviceStore.setFilter('partner', partnerParam);
    } else {
      // Clear partner filter if not in URL
      serviceStore.setFilter('partner', '');
    }

    // Fetch services with any URL filters applied
    serviceStore.fetchServices();
  }, [searchParams, i18n.language]);

  const handleSearch = (value) => {
    serviceStore.setFilter('search', value);
    serviceStore.fetchServices(1);
  };

  const handleCategoryFilter = (value) => {
    serviceStore.setFilter('service_category', value);
    serviceStore.fetchServices(1);
  };

  const handleCityFilter = (value) => {
    // Store normalized value for UI consistency
    serviceStore.setFilter('partner__city', value || '');
    serviceStore.fetchServices(1);
  };

  const handleSortChange = (value) => {
    serviceStore.setFilter('ordering', value);
    serviceStore.fetchServices(1);
  };

  const handleRatingFilter = (value) => {
    serviceStore.setFilter('min_rating', value);
    serviceStore.fetchServices(1);
  };

  const handleAnimalCategoryFilter = (value) => {
    serviceStore.setFilter('animal_category', value);
    serviceStore.fetchServices(1);
  };

  const handlePageChange = (page) => {
    serviceStore.fetchServices(page);
    window.scrollTo(0, 0);
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  const clearFilters = () => {
    serviceStore.clearFilters();
    setSearchValue('');
    // Clear URL params if partner filter was in URL
    if (searchParams.get('partner')) {
      navigate('/services', { replace: true });
    }
    serviceStore.fetchServices(1);
  };

  // Get localized category name
  const getCategoryName = (category) => {
    return i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en;
  };

  return (
    <div className="services-page">
      {/* Show back button when coming from partner profile */}
      {searchParams.get('partner') && (
        <div style={{ padding: '16px 24px 0' }}>
          <BackButton label={t('common.back')} />
        </div>
      )}

      <div className="services-header">
        <h1>{t('services.title')}</h1>
        <p>{t('services.description')}</p>
      </div>

      {serviceStore.error && (
        <Alert
          message={t('common.error')}
          description={serviceStore.error}
          type="error"
          closable
          onClose={() => serviceStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="services-filters">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={5}>
            <Search
              placeholder={t('services.searchPlaceholder')}
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
            />
          </Col>

          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('services.category')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              onChange={handleCategoryFilter}
              value={serviceStore.filters.service_category || undefined}
            >
              {serviceStore.categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {getCategoryName(category)}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('services.location')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={handleCityFilter}
              value={serviceStore.filters.partner__city || undefined}
            >
              {serviceStore.cities.map((city) => (
                <Option key={city} value={city}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />
                  {getCityByLanguage(city, i18n.language)}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('services.animalType')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={handleAnimalCategoryFilter}
              value={serviceStore.filters.animal_category || undefined}
            >
              {petStore.categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {getAnimalNameWithEmoji(i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en)}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('services.minRating')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              onChange={handleRatingFilter}
              value={serviceStore.filters.min_rating || undefined}
            >
              <Option value="4"><StarOutlined /> {t('services.stars4')}</Option>
              <Option value="3"><StarOutlined /> {t('services.stars3')}</Option>
              <Option value="2"><StarOutlined /> {t('services.stars2')}</Option>
              <Option value="1"><StarOutlined /> {t('services.stars1')}</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('services.sortBy')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              onChange={handleSortChange}
              value={serviceStore.filters.ordering || undefined}
            >
              <Option value="price">{t('services.sortPriceLowHigh')}</Option>
              <Option value="-price">{t('services.sortPriceHighLow')}</Option>
              <Option value="-average_rating">{t('services.sortRatingHighest')}</Option>
              <Option value="-created_at">{t('services.sortNewest')}</Option>
            </Select>
          </Col>

          <Col xs={24} sm={24} md={3} style={{ display: 'flex', alignItems: 'center' }}>
            <button className="clear-filters-btn" onClick={clearFilters}>
              {t('common.clear')}
            </button>
          </Col>
        </Row>
      </div>

      {serviceStore.loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : serviceStore.services.length === 0 ? (
        <Empty
          description={t('services.noServices')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} className="services-grid">
            {serviceStore.services.map((service) => (
              <Col xs={24} sm={12} md={8} lg={6} key={service.id}>
                <Card
                  hoverable
                  className="service-card"
                  onClick={() => handleServiceClick(service.id)}
                  cover={
                    <div className="service-card-header">
                      {service.partner_logo ? (
                        <img
                          src={service.partner_logo}
                          alt={service.partner_name}
                          className="partner-logo"
                        />
                      ) : (
                        <div className="partner-logo-placeholder">
                          <ShopOutlined />
                        </div>
                      )}
                      <div className="service-category-badge">
                        {i18n.language === 'el' ? (service.category_name_el || service.category_name_en) : service.category_name_en}
                      </div>
                      <div className="favorite-button-overlay">
                        <FavoriteButton
                          type="service"
                          itemId={service.id}
                          size="small"
                        />
                      </div>
                    </div>
                  }
                >
                  <div className="service-card-content">
                    <h3 className="service-name">
                      {i18n.language === 'el' ? (service.name_el || service.name_en) : service.name_en}
                    </h3>
                    <p className="service-description">
                      {(() => {
                        const desc = i18n.language === 'el' ? (service.description_el || service.description_en) : service.description_en;
                        return desc.length > 80 ? `${desc.substring(0, 80)}...` : desc;
                      })()}
                    </p>

                    {service.average_rating > 0 && (
                      <div className="service-rating">
                        <Rate disabled allowHalf value={parseFloat(service.average_rating)} style={{ fontSize: 14 }} />
                        <span className="rating-text">
                          {parseFloat(service.average_rating).toFixed(1)} ({service.rating_count})
                        </span>
                      </div>
                    )}

                    <div className="service-info">
                      <div className="info-item partner-name-row">
                        <ShopOutlined />
                        <span
                          className="partner-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/partners/${service.partner}`);
                          }}
                        >
                          {service.partner_name}
                        </span>
                        {service.partner_segment === 'FOUNDING' && (
                          <FoundingPartnerBadge size="small" />
                        )}
                        {service.partner_is_impact && (
                          <span className="impact-badge" title={t('services.impactPartner')} style={{ marginLeft: 4 }}>
                            🤝
                          </span>
                        )}
                      </div>
                      <div className="info-item partner-location">
                        <EnvironmentOutlined />
                        <span>{getCityByLanguage(service.partner_address?.city, i18n.language)}</span>
                      </div>
                    </div>

                    <div className="service-details">
                      <div className="detail-item">
                        <CalendarOutlined />
                        <span>{service.duration} min</span>
                      </div>
                      <div className="detail-item price">
                        <EuroOutlined />
                        <span className="price-amount">€{service.price}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {serviceStore.totalCount > serviceStore.pageSize && (
            <div className="pagination-container">
              <Pagination
                current={serviceStore.currentPage}
                total={serviceStore.totalCount}
                pageSize={serviceStore.pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => t('services.totalServices', { count: total })}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default ServicesPage;
